// Keep the review provider's nodes, events, validation and submission intact.
const instances = new WeakMap();
const widgetId = 'am-reviews-carousel-shadow-container-production';
const overlayId = 'am-reviews-overlay-container-production';
const prefix = name => `[class*="aftership-reviews-widget-v2_${name}--"]`;

export function initializeReviews(section, { timeoutMs = 12000, pollMs = 250 } = {}) {
  if (instances.has(section)) return instances.get(section);
  const doc = section?.ownerDocument;
  const win = doc?.defaultView;
  const source = section?.getAttribute('data-review-styles');
  if (!source || !win?.MutationObserver) return null;
  let url;
  try { url = new URL(source, doc.baseURI); } catch { return null; }
  if (!['https:', 'http:'].includes(url.protocol) ||
      (url.origin !== win.location.origin && url.hostname !== 'cdn.shopify.com')) return null;
  const slot = section.querySelector('[data-reno-review-form-slot]');
  if (!slot) return null;
  const status = section.querySelector('[data-review-status]');
  const invitation = section.querySelector('.reno-reviews-invitation');
  const roots = new Map();
  const listeners = [];
  const bound = new WeakSet();
  const overlays = new Map();
  let stopped = false, queued = false, opened = null, writeButton = null;
  let focusRequested = false, returnFocus = false, renderedBefore = false;
  let ratingFocus = null;
  let pollUntil = Date.now() + timeoutMs;

  function attr(node, key, value) {
    if (node && node.getAttribute(key) !== String(value)) node.setAttribute(key, value);
  }
  function listen(node, type, handler) {
    node.addEventListener(type, handler);
    listeners.push(() => node.removeEventListener(type, handler));
  }
  function schedule() {
    if (stopped || queued) return;
    queued = true;
    win.queueMicrotask(() => { queued = false; if (!stopped) refresh(); });
  }
  function observeRoot(host) {
    const root = host.shadowRoot;
    if (!roots.has(root)) {
      const link = doc.createElement('link');
      link.rel = 'stylesheet';
      link.href = url.href;
      link.setAttribute('data-reno-review-style', '');
      const state = { host, link, ready: false, failed: false };
      const observer = new win.MutationObserver(schedule);
      observer.observe(root, { subtree: true, childList: true, characterData: true,
        attributes: true, attributeFilter: ['style', 'width', 'disabled', 'class'] });
      state.observer = observer;
      roots.set(root, state);
      listen(link, 'load', () => { state.ready = true; state.failed = false; schedule(); });
      listen(link, 'error', () => { if (!state.ready) state.failed = true; schedule(); });
    }
    const state = roots.get(root);
    if (state.link.parentNode !== root) {
      state.ready = false; state.failed = false;
      host.removeAttribute('data-reno-review-widget'); host.removeAttribute('data-reno-review-form');
      root.append(state.link);
    }
    return state;
  }
  function keyboardButton(node, label, beforeClick = () => {}) {
    if (!node) return;
    attr(node, 'role', 'button'); attr(node, 'tabindex', '0'); attr(node, 'aria-label', label);
    if (bound.has(node)) return;
    bound.add(node);
    listen(node, 'click', beforeClick);
    listen(node, 'keydown', event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault(); node.click();
      }
    });
  }
  function enhanceForm(host) {
    const root = host.shadowRoot;
    const form = root.querySelector('form');
    const title = root.querySelector('#writeReviewCardTitle');
    if (form && title) attr(form, 'aria-labelledby', title.id);
    for (const name of ['name', 'email', 'content']) {
      const input = root.querySelector(`[name="${name}"]`);
      if (!input) continue;
      const label = input.parentElement.querySelector(prefix('label'));
      if (label) {
        if (!label.id) label.id = `RenoReviewLabel-${name}`;
        attr(input, 'aria-labelledby', label.id);
        attr(input, 'aria-required', label.textContent.includes('*'));
      }
      if (name !== 'content') attr(input, 'autocomplete', name);
      if (name === 'email') attr(input, 'inputmode', 'email');
      const error = input.parentElement.querySelector(`div${prefix('error')}`);
      if (error?.textContent.trim()) {
        if (!error.id) error.id = `RenoReviewError-${name}`;
        attr(input, 'aria-invalid', 'true'); attr(input, 'aria-describedby', error.id);
      } else {
        input.removeAttribute('aria-invalid'); input.removeAttribute('aria-describedby');
      }
    }
    const group = root.querySelector(`${prefix('productInfoContainer')} > div:last-child`);
    const stars = group ? [...group.children].filter(node => node.querySelector('svg')) : [];
    if (stars.length === 5) {
      attr(group, 'role', 'radiogroup'); attr(group, 'aria-label', 'Rating');
      const selected = stars.filter(star => parseFloat(star.querySelector('svg rect:last-of-type')?.getAttribute('width')) > 0).length;
      stars.forEach((star, index) => {
        attr(star, 'data-reno-review-rating', index + 1); attr(star, 'role', 'radio');
        attr(star, 'aria-label', `${index + 1} ${index ? 'stars' : 'star'}`);
        attr(star, 'aria-checked', index === selected - 1);
        attr(star, 'tabindex', index === Math.max(0, selected - 1) ? '0' : '-1');
        if (bound.has(star)) return;
        bound.add(star);
        listen(star, 'keydown', event => {
          const next = { ArrowRight: (index + 1) % 5, ArrowUp: (index + 1) % 5,
            ArrowLeft: (index + 4) % 5, ArrowDown: (index + 4) % 5, Home: 0, End: 4,
            Enter: index, ' ': index }[event.key];
          if (next === undefined) return;
          event.preventDefault();
          ratingFocus = { host, index: next };
          stars[next].click();
          schedule();
        });
        listen(star, 'click', schedule);
      });
      // The provider replaces its star nodes after each rating change.
      if (ratingFocus?.host === host) {
        stars[ratingFocus.index].focus({ preventScroll: true });
        ratingFocus = null;
      }
    }
    const close = root.querySelector('#writeReviewCardCloseButton');
    keyboardButton(close, 'Close review form', () => { returnFocus = true; });
    // The native upload tile is identified by its image, not its generated class hash.
    const upload = [...root.querySelectorAll('img[src^="data:image/svg+xml"]')]
      .find(img => img.getAttribute('width') === '24px')?.parentElement;
    keyboardButton(upload, 'Add photos or videos');
    if (form && !bound.has(form)) {
      bound.add(form);
      listen(form, 'keydown', event => {
        if (event.key === 'Escape' && close) { event.preventDefault(); close.click(); }
      });
    }
  }
  function restoreOverlay(overlay, original) {
    if (overlay.parentElement !== slot) return;
    if (original.next?.parentNode === original.parent) original.parent.insertBefore(overlay, original.next);
    else original.parent.append(overlay);
  }
  function refresh() {
    if (stopped) return;
    const host = section.querySelector(`#${widgetId}`);
    if (!host?.shadowRoot) {
      if (invitation) invitation.hidden = true;
      if (status && renderedBefore) {
        status.hidden = false;
        if (status.textContent !== 'Customer reviews are currently unavailable.') status.textContent = 'Customer reviews are currently unavailable.';
      }
      for (const [overlay, original] of overlays) restoreOverlay(overlay, original);
      return;
    }
    const state = observeRoot(host);
    const root = host.shadowRoot;
    const nativeButton = root.querySelector('#writeReviewButton');
    const empty = root.querySelector('#noReviewsWidgetText');
    const rendered = empty || root.querySelector('#overallRatingContainer');
    if (rendered) { renderedBefore = true; if (status) status.hidden = true; }
    if (invitation) invitation.hidden = !(empty && state.ready && !state.failed);
    if (state.ready && !state.failed) {
      attr(host, 'data-reno-review-widget', '');
      host.toggleAttribute('data-reno-review-empty', Boolean(empty));
      const wrapper = [...root.children].find(node => node.tagName === 'DIV');
      if (wrapper?.firstElementChild) attr(wrapper.firstElementChild, 'data-reno-review-surface', '');
      if (empty?.parentElement) attr(empty.parentElement, 'data-reno-review-empty-row', '');
    }
    writeButton = nativeButton;
    if (nativeButton && !bound.has(nativeButton)) {
      bound.add(nativeButton);
      listen(nativeButton, 'click', () => {
        focusRequested = true; pollUntil = Date.now() + timeoutMs; schedule();
      });
    }
    const overlay = doc.getElementById(overlayId);
    const formHost = overlay?.querySelector('#writeReviewCard');
    const validForm = formHost?.shadowRoot?.querySelector('form');
    if (validForm && state.ready && !state.failed) {
      const formState = observeRoot(formHost);
      if (formState.ready && !formState.failed) {
        attr(formHost, 'data-reno-review-form', '');
        enhanceForm(formHost);
        if (!overlays.has(overlay)) overlays.set(overlay, { parent: overlay.parentNode, next: overlay.nextSibling });
        // Solid's Portal cleanup captures overlay as the parent of writeReviewCard.
        // Move the whole mount, so close/reopen can still remove the native host.
        if (overlay.parentElement !== slot) slot.append(overlay);
        opened = formHost;
        attr(nativeButton, 'aria-expanded', 'true');
        if (focusRequested) {
          focusRequested = false;
          formHost.shadowRoot.querySelector('[name="name"]')?.focus({ preventScroll: true });
          formHost.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
        }
      } else if (overlays.has(overlay)) restoreOverlay(overlay, overlays.get(overlay));
    } else if (!validForm && opened) {
      opened = null;
      if (returnFocus) { writeButton?.focus({ preventScroll: true }); returnFocus = false; }
    }
    if (state.failed || !state.ready) {
      for (const [mount, original] of overlays) restoreOverlay(mount, original);
    }
    if (!opened) attr(nativeButton, 'aria-expanded', 'false');
  }
  const documentObserver = new win.MutationObserver(schedule);
  documentObserver.observe(doc.body, { childList: true, subtree: true });
  const timer = win.setInterval(() => { if (Date.now() < pollUntil) refresh(); }, pollMs);
  const unavailableTimer = win.setTimeout(() => {
    if (status && !status.hidden) status.textContent = 'Customer reviews are currently unavailable.';
  }, timeoutMs);
  const controller = { refresh, disconnect() {
    if (stopped) return;
    stopped = true;
    documentObserver.disconnect(); win.clearInterval(timer); win.clearTimeout(unavailableTimer);
    for (const [root, state] of roots) {
      state.observer.disconnect(); state.link.remove();
      state.host.removeAttribute('data-reno-review-widget'); state.host.removeAttribute('data-reno-review-form');
      state.host.removeAttribute('data-reno-review-empty');
      for (const node of root.querySelectorAll('[data-reno-review-surface], [data-reno-review-empty-row]')) {
        node.removeAttribute('data-reno-review-surface'); node.removeAttribute('data-reno-review-empty-row');
      }
    }
    for (const remove of listeners) remove();
    for (const [overlay, original] of overlays) restoreOverlay(overlay, original);
    instances.delete(section);
  } };
  instances.set(section, controller);
  refresh();
  return controller;
}

if (typeof document !== 'undefined') {
  const initialize = () => document.querySelectorAll('[data-reno-reviews]').forEach(section => initializeReviews(section));
  initialize();
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-reno-reviews]').forEach(section => instances.get(section)?.disconnect());
  });
  window.addEventListener('pagehide', () => {
    document.querySelectorAll('[data-reno-reviews]').forEach(section => instances.get(section)?.disconnect());
  });
  window.addEventListener('pageshow', initialize);
}
